<template>
  <div :title="`${action_name} ${module_name}`" button-accept="false" button-cancel="false" :active.sync="isActive">
    <div class="mb-4 flex items-center">
      <vs-button @click="isActive = false" color="dark" type="filled" icon-pack="feather" icon="icon-arrow-left"> Back </vs-button>
    </div>
    <form>
      <!-- Title -->
      <div class="mb-3">
        <vs-input
          icon-pack="feather"
          class="w-full"
          v-validate="'required|exclude_special_char'"
          v-model="form.title"
          :label="`${module_name} Title *`"
          placeholder="SGPC Administration / Officials"
          name="title"
          id="title"
          :disabled="!isEditable"
        />
        <span class="text-danger text-sm" v-show="errors.has('title')">{{ errors.first('title') }}</span>
      </div>

      <!-- Contact Details Section -->
      <div class="w-full">
        <div class="flex justify-between items-center mb-6">
          <h3 class="text-2xl font-semibold">{{ module_name }} Details</h3>
        </div>

        <div class="space-y-6">
          <div v-for="(detail, detailIndex) in form.details" :key="detailIndex" class="rounded-lg border border-gray-300 shadow-md m-6 p-6 bg-white">
            <div class="flex justify-between items-center mb-4">
              <h4 class="text-xl font-semibold">Contact Us {{ detailIndex + 1 }}</h4>
              <div class="flex gap-4">
                <vx-tooltip :text="`Remove Contact Us Detail`" v-if="isEditable">
                  <vs-button @click="removeDetail(detailIndex)" color="danger" type="gradient" icon-pack="feather" icon="icon-trash-2"></vs-button>
                </vx-tooltip>
              </div>
            </div>
            <!-- Detail Title -->
            <div class="w-full mb-4">
              <vs-input
                icon-pack="feather"
                type="text"
                v-validate="'exclude_special_char'"
                data-vv-as="Sub Title"
                v-model="detail.sub_title"
                label="Sub Title"
                placeholder="Enter title"
                :name="`sub_title_${detailIndex}`"
                class="w-full"
                :disabled="!isEditable"
              />
              <span class="text-danger text-sm" v-show="errors.has(`sub_title_${detailIndex}`)">
                {{ errors.first(`sub_title_${detailIndex}`) }}
              </span>
            </div>

            <!-- Custom Fields Section -->
            <div class="w-full mb-4">
              <div class="flex justify-between items-center mb-4">
                <h4 class="text-lg font-semibold">Custom Fields</h4>
                <vx-tooltip text="Add Custom Field" v-if="isEditable">
                  <vs-button @click="addCustomField(detailIndex)" color="dark" type="filled" icon-pack="feather" icon="icon-plus"></vs-button>
                </vx-tooltip>
              </div>
              <div v-if="detail.custom_fields && detail.custom_fields.length">
                <div v-for="(customField, customIndex) in detail.custom_fields" :key="customIndex" class="custom-field-entry flex flex-wrap items-start gap-4 my-4">
                  <!-- Field Type -->
                  <div class="vx-col w-full md:w-4/12 px-2 mb-2">
                    <label class="vs-input--label">Type *</label>
                    <select-2
                      class="w-full country-input"
                      :name="`custom_field_type_${detailIndex}_${customIndex}`"
                      @input="(item) => (customField.type = item && item.value)"
                      placeholder="Enter Type"
                      autocomplete
                      :ssr="true"
                      :multiple="false"
                      v-model="customField.type"
                      v-validate="'required'"
                      :viewRendered="false"
                      data-vv-as="Type"
                      :options="typeOptions"
                      :disabled="!isEditable"
                    />
                    <span class="text-danger text-sm" v-show="errors.has(`custom_field_type_${detailIndex}_${customIndex}`)">
                      {{ errors.first(`custom_field_type_${detailIndex}_${customIndex}`) }}
                    </span>
                  </div>

                  <!-- Field Label -->
                  <div class="vx-col w-full md:w-3/12 px-2 mb-2">
                    <vs-input
                      v-model="customField.label"
                      label="Label *"
                      :name="`custom_field_label_${detailIndex}_${customIndex}`"
                      v-validate="'required|exclude_special_char'"
                      data-vv-as="Custom Label"
                      placeholder="Enter label"
                      class="w-full"
                      :disabled="!isEditable"
                    />
                    <span class="text-danger text-sm" v-show="errors.has(`custom_field_label_${detailIndex}_${customIndex}`)">
                      {{ errors.first(`custom_field_label_${detailIndex}_${customIndex}`) }}
                    </span>
                  </div>

                  <!-- Dynamic Field Value -->
                  <div class="vx-col w-full md:w-3/12 px-2 mb-2">
                    <!-- Conditional Rendering Based on customField.type -->
                    <vs-input
                      v-if="customField.type === 'name' || customField.type === 'other'"
                      v-model="customField.value"
                      label="Field Value *"
                      :name="`custom_field_value_${detailIndex}_${customIndex}`"
                      v-validate="'required|exclude_special_char'"
                      data-vv-as="Field Value"
                      placeholder="Field value"
                      class="w-full"
                      :disabled="!isEditable"
                    />

                    <vs-input
                      v-else-if="customField.type === 'email'"
                      type="email"
                      v-model="customField.value"
                      label="Field Value *"
                      :name="`custom_field_value_${detailIndex}_${customIndex}`"
                      v-validate="'required|email'"
                      data-vv-as="Email"
                      placeholder="Enter email"
                      class="w-full"
                      :disabled="!isEditable"
                    />

                    <div v-else-if="customField.type === 'mobile_number'">
                      <label class="vs-input--label">Field Value *</label>
                      <!-- Mobile Number Input with vue-tel-input -->
                      <vue-tel-input
                        v-model="customField.value"
                        class="w-full vs-inputx vs-input--input p-1"
                        placeholder="Enter mobile number"
                        v-validate="'required'"
                        data-vv-as="Mobile Number"
                        :name="`custom_field_value_${detailIndex}_${customIndex}`"
                        :disabled="!isEditable"
                      />
                    </div>

                    <vs-input
                      v-else-if="customField.type === 'location' || customField.type === 'website'"
                      type="url"
                      v-model="customField.value"
                      label="Field Value *"
                      :name="`custom_field_value_${detailIndex}_${customIndex}`"
                      v-validate="'required|exclude_special_char|url'"
                      data-vv-as="URL"
                      placeholder="Enter URL"
                      class="w-full"
                      :disabled="!isEditable"
                    />

                    <vs-input
                      v-else-if="customField.type === 'fax' || customField.type === 'exth'"
                      type="number"
                      v-model="customField.value"
                      label="Field Value *"
                      :name="`custom_field_value_${detailIndex}_${customIndex}`"
                      v-validate="'required|numeric'"
                      data-vv-as="Number"
                      placeholder="Enter number"
                      class="w-full"
                      :disabled="!isEditable"
                    />

                    <span class="text-danger text-sm" v-show="errors.has(`custom_field_value_${detailIndex}_${customIndex}`)">
                      {{ errors.first(`custom_field_value_${detailIndex}_${customIndex}`) }}
                    </span>
                  </div>

                  <!-- Remove Custom Field Button -->
                  <div class="w-full md:w-1/12 px-2 mb-2 flex items-center mt-6" v-if="isEditable">
                    <vx-tooltip text="Remove Custom Field">
                      <vs-button @click="removeCustomField(detailIndex, customIndex)" color="danger" type="gradient" icon-pack="feather" icon="icon-trash-2" />
                    </vx-tooltip>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Add Detail Button -->
      </div>

      <!-- Save & Cancel Buttons -->
      <div class="mt-6 flex justify-between space-x-2">
        <div v-if="isEditable">
          <vs-button color="warning" @click="addDetail" size="default">Add Contact Detail</vs-button>
        </div>
        <div>
          <vs-button class="mr-2 vs-con-loading__container" id="create-contact" @click="save_changes" v-if="isEditable">Save Changes</vs-button>
          <vs-button color="danger" @click="navigateList" class="text-white-500"> Cancel </vs-button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import Select2 from '@/components/custom/form-elements/Select2.vue' 
export default {
  /** Component Name */
  name: 'AddUpdateContact',

  /** Props */
  props: {
    selectedRecordId: String,
    module_name: String,
    action_name: String,
    showModal: Boolean
  },

  components: {
    Select2
  },

  /** Data */
  data() {
    return {
      form: {
        title: '',
        type: 'Details',
        organization: this.$route.params.id,
        details: []
      },
      typeOptions: [
        { value: 'name', label: 'Name' },
        { value: 'email', label: 'Email' },
        { value: 'mobile_number', label: 'Mobile Number' },
        { value: 'location', label: 'Location' },
        { value: 'website', label: 'Website' },
        { value: 'fax', label: 'Fax' },
        { value: 'exth', label: 'Extension' },
        { value: 'other', label: 'Other' }
      ]
    }
  },

  /** Computed Properties */
  computed: {
    ...mapState('organization', ['createLoading']),
    ...mapGetters('auth', ['checkPermissionSlug']),
    validateForm() {
      return !this.errors.any()
    },
    isEditable() {
      return this.checkPermissionSlug('organization_contact_edit')
    },
    isActive: {
      get() {
        return this.showModal
      },
      set(val) {
        this.$emit('update:showModal', val)
      }
    }
  },

  /** Lifecycle Hooks */
  async created() {
    if (this.selectedRecordId) {
      try {
        const res = await this.$store.dispatch('organization/contactShow', this.selectedRecordId)
        const data = res.data.data

        /* Initialize form with fetched data */
        this.form.title = data.title || ''
        this.form.type = data.type || 'Details'
        this.form.organization = data.organization || this.$route.params.id
        this.form.details = (data.details || []).map((detail) => ({
          sub_title: detail.sub_title || '',
          custom_fields: (detail.custom_fields || []).map((cf) => ({
            type: cf.type || '',
            label: cf.label || '',
            value: cf.value || ''
          }))
        }))
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: error.message || 'Failed to load contact details.',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center',
          time: 5000,
          color: 'primary'
        })
      }
    } else {
      this.form.details.push({
        sub_title: '',
        custom_fields: []
      })
    }
  },

  /** Methods */
  methods: {
    ...mapActions('organization', {
      create: 'contactCreate',
      update: 'contactUpdate'
    }),
    navigateList() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: `Confirm Cancel`,
        text: `Are you sure you want to cancel?`,
        accept: () => {
          this.isActive = false
        },
        acceptText: 'Yes',
        cancelText: 'No'
      })
    },
    /**
     * Save Changes Handler
     */
    async save_changes() {
      // Validate the entire form
      const isValid = await this.$validator.validateAll()
      if (!isValid) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please fix the errors first.',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center',
          time: 5000,
          color: 'primary'
        })
        return
      }

      // Construct payload
      const payload = {
        type: this.form.type,
        organization: this.form.organization,
        title: this.form.title,
        details: this.form.details.map((detail) => ({
          sub_title: detail.sub_title,
          custom_fields: detail.custom_fields.map((cf) => ({
            type: cf.type,
            label: cf.label,
            value: cf.value
          }))
        }))
      }

      try {
        let response
        if (this.selectedRecordId) {
          // Update existing contact
          response = await this.update({
            id: this.selectedRecordId,
            data: payload
          })
        } else {
          // Create new contact
          response = await this.create(payload)
        }

        // Close modal and notify success
        this.isActive = false
        this.$emit('update-data', true)
        this.$vs.notify({
          title: 'Success',
          text: response.message || 'Operation successful.',
          iconPack: 'feather',
          icon: 'icon-check',
          position: 'top-center',
          time: 5000,
          color: 'success'
        })
      } catch (error) {
        // Notify error
        this.$vs.notify({
          title: 'Error',
          text: error.message || 'An error occurred.',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-center',
          time: 5000,
          color: 'primary'
        })
      }
    },

    /**
     * Add a new Contact Detail
     */
    addDetail() {
      this.form.details.push({
        sub_title: '',
        custom_fields: []
      })
    },

    /**
     * Remove a Contact Detail
     * @param {Number} detailIndex - Index of the detail to remove
     */
    removeDetail(detailIndex) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: `Confirm Delete`,
        text: `Are you sure you want to delete?`,
        accept: () => {
          this.form.details.splice(detailIndex, 1)
        },
        acceptText: 'Delete'
      })
    },

    /**
     * Add a Custom Field to a specific Detail
     * @param {Number} detailIndex - Index of the detail
     */
    addCustomField(detailIndex) {
      const detail = this.form.details[detailIndex]
      if (!detail.custom_fields) {
        this.$set(detail, 'custom_fields', [])
      }
      detail.custom_fields.push({
        type: 'other',
        label: '',
        value: ''
      })
    },

    /**
     * Remove a Custom Field from a specific Detail
     * @param {Number} detailIndex - Index of the detail
     * @param {Number} customIndex - Index of the custom field
     */
    removeCustomField(detailIndex, customIndex) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: `Confirm Delete`,
        text: `Are you sure you want to delete?`,
        accept: () => {
          const detail = this.form.details[detailIndex]
          detail.custom_fields.splice(customIndex, 1)
        },
        acceptText: 'Delete'
      })
    }
  },

  /** Watchers */
  watch: {
    createLoading(newVal) {
      if (newVal) {
        this.$vs.loading({
          container: '#create-contact',
          scale: 0.45
        })
      } else {
        this.$vs.loading.close('#create-contact > .con-vs-loading')
      }
    }
  }
}
</script>

<style scoped>
/* Scoped styles for the component */

.custom-field-entry {
  background-color: #ffffff;
  padding: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.vx-col {
  display: flex;
  flex-direction: column;
}
</style>
